import type { ToolExecutionPolicy } from "../types";

/**
 * Single source for the chart route matrix. Every prompt that can influence
 * chart routing includes this block, so the model never sees two descriptions
 * of the same decision that could drift apart.
 *
 * Only Preslink creation adds a route to decide between, so with that gate
 * closed there is no block at all and the ActiveGraph prompts stay exactly as
 * they were before Preslink creation existed.
 */
export function buildChartRouteMatrixPrompt(policy?: ToolExecutionPolicy): string {
  if (!policy?.allowPreslinkChartCreation) return "";
  const activeGraph = policy.allowActiveGraph === true;

  const explicitActiveGraph = activeGraph
    ? `- Explicit "live chart", "live FactSet chart", or "ActiveGraph" request: active_graph, even when create_preslink_chart is the default.`
    : `- Explicit "live chart" or "ActiveGraph" request: that route is unavailable on this turn; say so rather than silently delivering another form.`;

  const userVisibleNames = [
    `a create_preslink_chart result a "linked FactSet chart"`,
    ...(activeGraph ? [`an active_graph result a "live FactSet chart"`] : []),
    `a native chart an "editable PowerPoint chart"`,
  ];
  const namingRule = `- Route names are internal. User-visible text calls ${
    userVisibleNames.length > 2
      ? `${userVisibleNames.slice(0, -1).join(", ")}, and ${userVisibleNames[userVisibleNames.length - 1]}`
      : userVisibleNames.join(" and ")
  }.`;

  return `## Chart route matrix

This matrix only decides how a NEW chart is delivered. Existing shapes marked "preslinkLinked", including shapes marked "preslinkChart", keep using apply_preslink for data updates; a "preslinkChart" shape is rebuilt with create_preslink_chart and target.shapeId only when a chart setting such as its chart type, theme, or colors changes.

Pick exactly ONE delivery route per new chart, in this order:

- DEFAULT for a catalog chart: create_preslink_chart. Use it whenever the request matches an enabled FactSet parameter-chart type and the user has not named a delivery form. The user does not need to say "Preslink".
${explicitActiveGraph}
- A named FactSet chart theme (Quartz, Carbon, Classic, Pearl, or another styleset) without an explicit live-chart request: create_preslink_chart with chartParameters.stylesetId set to that theme.
- Explicit "Excel chart", "editable", "native", or "PowerPoint chart" request: the native chart route (shape.chart).
- A chart form word (pie, line, bar, column) names neither a delivery route nor a native request. FactSet types draw their own form: revenue by business or geographic segment is a FactSet pie, so "a pie chart of revenue by segment, region, or continent" is a catalog chart.
- Explicit "Preslink" or "linked FactSet chart" request: create_preslink_chart.
- Request outside the enabled FactSet chart catalog: route exactly as you would today. A new route never widens what can be charted.

Route discipline:
- A chosen route never falls back after a failure. If the selected route fails, report the failure; do not quietly deliver a different kind of chart.
- A preslink_chart_use_refresh result is a redirect, not a failure: follow its message.
- Never create the same series twice through two routes on one slide.
${namingRule}

Before create_preslink_chart:
- Resolve company names with id_lookup in an earlier hop; use candidate.id.
- Use list_active_graph_types or ask when the metric or chart type is unnamed. Never guess the type.
- Send validated chartParameters and, at most, a target slide, a shape to replace, and a frame (left, top, width, height in points) when the user gives a position or size. A frame without a shapeId always adds a new chart. Without a frame the tool decides size and position. Never send file paths, filenames, shape tags, or binary content — the tool decides where anything is written.
- Do not call it only to move or resize a linked chart, which rebuilds its workbook; the user can drag or resize it in PowerPoint.
- The chart goes on the current slide. Create a new slide only when the user asks for one, and observe it before passing its real slideId.`;
}
